import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { formatPhoneNumber } from "../../utils/phone";
import "../../styles/OwnerOperatorCompanyPage.css";

const EMPTY_FORM = {
  companyName: "",
  companyId: "",
  mainContact: "",
  lastName: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  postalCode: "",
  phones: [],
  emails: [],
  taxes: [],
  workersCompensationType: "",
  workersCompensationNumber: "",
  workersCompensationAlternative: "",
  workersCompensationRenewal: "",
};

const EMAIL_TYPES = ["Company", "Email", "Other", "Personal"];
const PHONE_TYPES = ["Phone", "Mobile", "Home", "Work"];
const TAX_TYPES = ["GST", "HST", "Other", "PST"];
const ALTERNATIVE_TYPES = ["NAL", "NTL", "Other"];

const OwnerOperatorCompanyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    axios
      .get(`${apiBase}/api/owner-operator-company/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(({ data }) => {
        if (active) setForm({ ...EMPTY_FORM, ...data });
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError?.response?.data?.message || "Unable to load company information.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [apiBase, id]);

  const setValue = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const addRow = (field, row) => setForm((current) => ({ ...current, [field]: [...current[field], row] }));
  const updateRow = (field, index, key, value) =>
    setForm((current) => ({
      ...current,
      [field]: current[field].map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  const removeRow = (field, index) =>
    setForm((current) => ({
      ...current,
      [field]: current[field].filter((_, rowIndex) => rowIndex !== index),
    }));

  const handleWorkersCompensationType = (value) => {
    setForm((current) => ({
      ...current,
      workersCompensationType: value,
      workersCompensationNumber: value === "Workers Comp" ? current.workersCompensationNumber : "",
      workersCompensationAlternative: value === "Alternative" ? current.workersCompensationAlternative : "",
      workersCompensationRenewal: value ? current.workersCompensationRenewal : "",
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.put(`${apiBase}/api/owner-operator-company/${id}`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      window.dispatchEvent(
        new CustomEvent("owner-operator-company-updated", {
          detail: { userId: id, companyName: form.companyName },
        }),
      );
      alert("Company information saved successfully");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to save company information.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container py-5">Loading company information...</div>;

  return (
    <div className="container-fluid px-2 owner-operator-company-page">
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span className="page-heading">Owner Operator Company Information</span>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(`/userhome/${id}`)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <h3 className="text-center mb-4">Company Information</h3>
        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSave}>
          <section className="company-info-section">
            <h4>Company</h4>
            <div className="row g-3">
              <div className="col-md-6"><label>Company Name</label><input className="form-control" value={form.companyName} onChange={(e) => setValue("companyName", e.target.value)} /></div>
              <div className="col-md-6"><label>Company ID #</label><input className="form-control" value={form.companyId} onChange={(e) => setValue("companyId", e.target.value)} placeholder="Business Registration #" /></div>
              <div className="col-md-6"><label>Main Contact</label><input className="form-control" value={form.mainContact} onChange={(e) => setValue("mainContact", e.target.value)} /></div>
              <div className="col-md-6"><label>Last Name</label><input className="form-control" value={form.lastName} onChange={(e) => setValue("lastName", e.target.value)} /></div>
            </div>
          </section>

          <section className="company-info-section">
            <h4>Phone and Email</h4>
            <div className="repeater-heading"><span><i className="fas fa-phone" /> Phone Numbers</span><button type="button" className="btn btn-sm btn-primary" onClick={() => addRow("phones", { type: "Phone", number: "", extension: "" })}><i className="fas fa-plus" /> Add Phone</button></div>
            {form.phones.length === 0 && <p className="text-muted">No phone numbers added.</p>}
            {form.phones.map((phone, index) => (
              <div className="repeat-row phone-row" key={`phone-${index}`}>
                <select className="form-select" value={phone.type} onChange={(e) => updateRow("phones", index, "type", e.target.value)}>{PHONE_TYPES.map((type) => <option key={type}>{type}</option>)}</select>
                <input className="form-control" value={formatPhoneNumber(phone.number)} onChange={(e) => updateRow("phones", index, "number", formatPhoneNumber(e.target.value))} placeholder="(000) 000-0000" inputMode="tel" />
                <input className="form-control" value={phone.extension} onChange={(e) => updateRow("phones", index, "extension", e.target.value)} placeholder="Extension" />
                <button type="button" className="btn btn-outline-danger icon-button" onClick={() => removeRow("phones", index)} title="Remove phone"><i className="fas fa-trash" /></button>
              </div>
            ))}

            <div className="repeater-heading mt-4"><span><i className="fas fa-envelope" /> Email Addresses</span><button type="button" className="btn btn-sm btn-primary" onClick={() => addRow("emails", { type: "Email", address: "" })}><i className="fas fa-plus" /> Add Email</button></div>
            {form.emails.length === 0 && <p className="text-muted">No email addresses added.</p>}
            {form.emails.map((email, index) => (
              <div className="repeat-row email-row" key={`email-${index}`}>
                <select className="form-select" value={email.type} onChange={(e) => updateRow("emails", index, "type", e.target.value)}>{EMAIL_TYPES.map((type) => <option key={type}>{type}</option>)}</select>
                <input type="email" className="form-control" value={email.address} onChange={(e) => updateRow("emails", index, "address", e.target.value)} placeholder="Email address" />
                <button type="button" className="btn btn-outline-danger icon-button" onClick={() => removeRow("emails", index)} title="Remove email"><i className="fas fa-trash" /></button>
              </div>
            ))}
          </section>

          <section className="company-info-section">
            <h4>Address</h4>
            <div className="row g-3">
              <div className="col-md-6"><label>Address 1</label><input className="form-control" value={form.address1} onChange={(e) => setValue("address1", e.target.value)} /></div>
              <div className="col-md-6"><label>Address 2</label><input className="form-control" value={form.address2} onChange={(e) => setValue("address2", e.target.value)} /></div>
              <div className="col-md-6"><label>City</label><input className="form-control" value={form.city} onChange={(e) => setValue("city", e.target.value)} /></div>
              <div className="col-md-3"><label>Province</label><input className="form-control" value={form.province} onChange={(e) => setValue("province", e.target.value)} /></div>
              <div className="col-md-3"><label>Postal Code</label><input className="form-control" value={form.postalCode} onChange={(e) => setValue("postalCode", e.target.value)} /></div>
            </div>
          </section>

          <section className="company-info-section">
            <div className="repeater-heading"><h4>Tax</h4><button type="button" className="btn btn-sm btn-primary" onClick={() => addRow("taxes", { number: "", active: true, type: "GST" })}><i className="fas fa-plus" /> Add Tax</button></div>
            {form.taxes.length === 0 && <p className="text-muted">No tax records added.</p>}
            {form.taxes.map((tax, index) => (
              <div className="repeat-row tax-row" key={`tax-${index}`}>
                <input className="form-control" value={tax.number} onChange={(e) => updateRow("taxes", index, "number", e.target.value)} placeholder="Tax #" />
                <label className="tax-active"><input type="checkbox" checked={Boolean(tax.active)} onChange={(e) => updateRow("taxes", index, "active", e.target.checked)} /> Active</label>
                <select className="form-select" value={tax.type} onChange={(e) => updateRow("taxes", index, "type", e.target.value)}>{TAX_TYPES.map((type) => <option key={type}>{type}</option>)}</select>
                <button type="button" className="btn btn-outline-danger icon-button" onClick={() => removeRow("taxes", index)} title="Remove tax"><i className="fas fa-trash" /></button>
              </div>
            ))}
          </section>

          <section className="company-info-section">
            <div className="workers-heading">
              <h4>{form.workersCompensationType === "Alternative" ? "Workers Compensation Alternative" : "Workers Compensation"}</h4>
              <select className="form-select" value={form.workersCompensationType} onChange={(e) => handleWorkersCompensationType(e.target.value)}>
                <option value="">Choose Type (if applicable)</option>
                <option value="Workers Comp">Workers Comp</option>
                <option value="Alternative">Alternative</option>
              </select>
            </div>
            {form.workersCompensationType === "Workers Comp" && (
              <div className="row g-3"><div className="col-md-6"><label>Workers Comp #</label><input className="form-control" value={form.workersCompensationNumber} onChange={(e) => setValue("workersCompensationNumber", e.target.value)} /></div><div className="col-md-6"><label>Renewal</label><input type="date" className="form-control" value={form.workersCompensationRenewal} onChange={(e) => setValue("workersCompensationRenewal", e.target.value)} /></div></div>
            )}
            {form.workersCompensationType === "Alternative" && (
              <div className="row g-3"><div className="col-md-6"><label>Alternative</label><select className="form-select" value={form.workersCompensationAlternative} onChange={(e) => setValue("workersCompensationAlternative", e.target.value)}><option value="">Select Workers Comp</option>{ALTERNATIVE_TYPES.map((type) => <option key={type}>{type}</option>)}</select></div><div className="col-md-6"><label>Renewal</label><input type="date" className="form-control" value={form.workersCompensationRenewal} onChange={(e) => setValue("workersCompensationRenewal", e.target.value)} /></div></div>
            )}
          </section>

          <div className="d-flex justify-content-end mt-4"><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button></div>
        </form>
      </div>
    </div>
  );
};

export default OwnerOperatorCompanyPage;
